/*
  GLASARI shared product template behaviour:
  gallery + thumbnails with hover and pinch zoom, full-screen lightbox with dots
  and zoom, 3D models with AR, colour/variant picker, quantity steppers, mobile sticky
  Add to cart bar, feature hotspots and silent looping story videos.
  Add to cart itself is handled by the theme's <product-form-component>, which
  opens the existing cart drawer.
*/
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = reduceMotion ? 'auto' : 'smooth';

  /** Index of the slide nearest the left edge of a scroll-snap track. */
  const currentIndex = (track) => {
    const width = track.clientWidth || 1;
    return Math.round(track.scrollLeft / width);
  };

  const scrollToIndex = (track, index, smooth = true) => {
    const slide = [...track.children].filter((el) => !el.hidden)[index];
    if (!slide) return;
    // Tracks are position: relative, so offsetLeft is measured from the track itself.
    track.scrollTo({ left: slide.offsetLeft, behavior: smooth ? behavior : 'auto' });
  };

  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  /*
    Zoom engine. Scales an <img> with translate + scale (transform-origin top
    left) inside its own layout box, so the zoomed image never drifts outside
    its frame. Used by the main gallery and the lightbox.
  */
  function createZoom(img, { max = 4 } = {}) {
    let s = 1;
    let x = 0;
    let y = 0;

    const size = () => ({ w: img.offsetWidth, h: img.offsetHeight });
    // Untransformed top-left corner on screen. Measured from the (positioned)
    // slide plus the image's layout offset, so a running zoom animation can't skew it.
    const origin = () => {
      const parent = img.offsetParent;
      if (!parent) {
        const rect = img.getBoundingClientRect();
        return { left: rect.left - x, top: rect.top - y };
      }
      const rect = parent.getBoundingClientRect();
      return {
        left: rect.left + parent.clientLeft + img.offsetLeft,
        top: rect.top + parent.clientTop + img.offsetTop,
      };
    };

    const clamp = () => {
      const { w, h } = size();
      x = Math.min(0, Math.max(w - w * s, x));
      y = Math.min(0, Math.max(h - h * s, y));
    };

    const apply = (animate) => {
      img.classList.toggle('is-animating', Boolean(animate));
      img.style.transform = s === 1 && x === 0 && y === 0 ? '' : `translate(${x}px, ${y}px) scale(${s})`;
    };

    // Ask the browser for a larger file from srcset the first time we zoom.
    const sharpen = () => {
      if (img.dataset.gxSharp) return;
      img.dataset.gxSharp = 'true';
      if (img.srcset) img.sizes = `${Math.ceil(img.offsetWidth * 2.5)}px`;
    };

    return {
      get scale() {
        return s;
      },
      /** Zoom to an absolute scale, keeping the screen point (cx, cy) still. */
      zoomAt(cx, cy, scale, animate = true) {
        const o = origin();
        const px = cx - o.left;
        const py = cy - o.top;
        const lx = (px - x) / s;
        const ly = (py - y) / s;
        s = Math.min(max, Math.max(1, scale));
        x = px - lx * s;
        y = py - ly * s;
        if (s > 1) sharpen();
        clamp();
        apply(animate);
      },
      zoomCenter(scale, animate = true) {
        const rect = img.getBoundingClientRect();
        this.zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, scale, animate);
      },
      panBy(dx, dy) {
        x += dx;
        y += dy;
        clamp();
        apply(false);
      },
      /** Desktop: map the pointer position across the image to the full pan range. */
      follow(cx, cy) {
        const o = origin();
        const { w, h } = size();
        const fx = Math.min(1, Math.max(0, (cx - o.left) / w));
        const fy = Math.min(1, Math.max(0, (cy - o.top) / h));
        x = -fx * (w * s - w);
        y = -fy * (h * s - h);
        apply(false);
      },
      reset(animate = true) {
        s = 1;
        x = 0;
        y = 0;
        apply(animate);
      },
    };
  }

  /** Two-finger pinch (plus optional one-finger pan and double-tap) for a zoom. */
  function attachTouchZoom(target, zoom, { panWhenZoomed = false, onStart, onEnd, onDoubleTap } = {}) {
    let pinch = null;
    let pan = null;
    let moved = false;
    let lastTap = { time: 0, x: 0, y: 0 };

    const distance = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const midpoint = (a, b) => [(a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2];
    const startPan = (touch) => {
      pan = panWhenZoomed && zoom.scale > 1 ? { x: touch.clientX, y: touch.clientY } : null;
    };

    target.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length === 2) {
          const [mx, my] = midpoint(event.touches[0], event.touches[1]);
          pinch = { d: distance(event.touches[0], event.touches[1]), s: zoom.scale, mx, my };
          pan = null;
          moved = true;
          onStart?.();
        } else if (event.touches.length === 1) {
          moved = false;
          startPan(event.touches[0]);
        }
      },
      { passive: true }
    );

    target.addEventListener(
      'touchmove',
      (event) => {
        if (pinch && event.touches.length === 2) {
          event.preventDefault();
          const [mx, my] = midpoint(event.touches[0], event.touches[1]);
          zoom.zoomAt(mx, my, (pinch.s * distance(event.touches[0], event.touches[1])) / pinch.d, false);
          zoom.panBy(mx - pinch.mx, my - pinch.my);
          pinch.mx = mx;
          pinch.my = my;
        } else if (pan && event.touches.length === 1) {
          event.preventDefault();
          const touch = event.touches[0];
          zoom.panBy(touch.clientX - pan.x, touch.clientY - pan.y);
          pan = { x: touch.clientX, y: touch.clientY };
          moved = true;
        } else {
          moved = true;
        }
      },
      { passive: false }
    );

    const finish = (event) => {
      if (pinch && event.touches.length < 2) {
        pinch = null;
        onEnd?.();
        if (event.touches.length === 1) startPan(event.touches[0]);
        return;
      }
      if (event.touches.length > 0) return;
      pan = null;

      if (!onDoubleTap || moved || event.changedTouches.length !== 1) return;
      const touch = event.changedTouches[0];
      const now = Date.now();
      if (now - lastTap.time < 300 && Math.hypot(touch.clientX - lastTap.x, touch.clientY - lastTap.y) < 30) {
        event.preventDefault();
        lastTap = { time: 0, x: 0, y: 0 };
        onDoubleTap(touch.clientX, touch.clientY);
      } else {
        lastTap = { time: now, x: touch.clientX, y: touch.clientY };
      }
    };
    target.addEventListener('touchend', finish);
    target.addEventListener('touchcancel', finish);
  }

  function initGallery(root) {
    const gallery = root.querySelector('[data-gx-gallery]');
    if (!gallery) return null;

    const track = gallery.querySelector('[data-gx-track]');
    const slides = [...track.querySelectorAll('[data-gx-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-gx-thumb]')];
    const thumbStrip = gallery.querySelector('[data-gx-thumbs]');
    const counter = gallery.querySelector('[data-gx-counter]');
    const totalEl = gallery.querySelector('[data-gx-total]');
    const prev = gallery.querySelector('[data-gx-prev]');
    const next = gallery.querySelector('[data-gx-next]');
    let active = 0;
    // Slides and thumbnails currently shown (all of them until a colour is chosen).
    let vSlides = slides;
    let vThumbs = thumbs;

    const setActive = (index) => {
      if (index === active && vThumbs[index]?.getAttribute('aria-current') === 'true') return;
      active = index;
      thumbs.forEach((thumb) => thumb.setAttribute('aria-current', 'false'));
      vThumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
      if (counter) counter.textContent = String(index + 1);
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === vSlides.length - 1;

      const thumb = vThumbs[index];
      if (thumb && thumbStrip) {
        // Measured from the strip itself, so hidden thumbnails and positioning cannot throw it off.
        const offset = thumb.getBoundingClientRect().left - thumbStrip.getBoundingClientRect().left + thumbStrip.scrollLeft;
        const left = offset - (thumbStrip.clientWidth - thumb.offsetWidth) / 2;
        thumbStrip.scrollTo({ left, behavior });
      }

      // Pause any video or 3D model that is no longer on screen.
      vSlides.forEach((slide, i) => {
        if (i === index) return;
        slide.querySelectorAll('video').forEach((video) => video.pause());
        slide.querySelector('[data-gx-model]')?.gxModelUI?.pause?.();
      });
    };

    let ticking = false;
    track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          setActive(Math.min(vSlides.length - 1, Math.max(0, currentIndex(track))));
          ticking = false;
        });
      },
      { passive: true }
    );

    const go = (index) => scrollToIndex(track, Math.min(vSlides.length - 1, Math.max(0, index)));

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => go(vThumbs.indexOf(thumb))));
    prev?.addEventListener('click', () => go(active - 1));
    next?.addEventListener('click', () => go(active + 1));

    track.addEventListener('keydown', (event) => {
      // Arrow keys rotate a 3D model when it has focus.
      if (event.target.closest('model-viewer')) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        go(active + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        go(active - 1);
      }
    });

    // Zoom on image slides: hover magnifier with a mouse, pinch on touch screens.
    slides.forEach((slide) => {
      const img = slide.querySelector('[data-gx-zoom-img]');
      if (!img) return;
      const zoom = createZoom(img, { max: 4 });

      slide.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' || !canHover.matches) return;
        slide.classList.add('is-hover-zoom');
        zoom.zoomAt(event.clientX, event.clientY, 2.2, true);
      });
      slide.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'mouse' && slide.classList.contains('is-hover-zoom')) {
          zoom.follow(event.clientX, event.clientY);
        }
      });
      slide.addEventListener('pointerleave', () => {
        if (!slide.classList.contains('is-hover-zoom')) return;
        slide.classList.remove('is-hover-zoom');
        zoom.reset(true);
      });

      // Pinch zooms in place and springs back on release, like a photo app.
      attachTouchZoom(slide, zoom, {
        onStart: () => slide.classList.add('is-pinching'),
        onEnd: () => {
          slide.classList.remove('is-pinching');
          zoom.reset(true);
        },
      });
    });

    setActive(0);

    const normalise = (text) => ' ' + String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() + ' ';

    // Every spelling a colour can appear under in a photo description.
    const colourForms = (name) => {
      const full = normalise(name).trim();
      const forms = new Set([full]);
      forms.add(full.replace(/ stripes?$/, ''));
      forms.add(full.replace(/s$/, ''));
      forms.add(full.replace(/ stripes?$/, '').replace(/s$/, ''));
      return [...forms].filter(Boolean);
    };

    return {
      firstMediaId() {
        return slides[0] ? slides[0].dataset.mediaId : null;
      },

      showMedia(mediaId) {
        const index = vSlides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index > -1) go(index);
      },

      /*
        Show only the photos for the chosen colour plus shared photos, judged by each photo's
        description: it names the colour (show), names no colour at all (shared, show) or names
        only other colours (hide).
      */
      filterColour(selected, allColours, focusMediaId) {
        const forms = allColours.map(colourForms);
        const picked = allColours.indexOf(selected);
        if (picked < 0) return;
        const visibleIds = new Set();

        slides.forEach((slide, i) => {
          const alt = normalise(slide.dataset.alt);
          const named = forms.map((variants) => variants.some((form) => alt.includes(' ' + form + ' ')));
          // The variant's own linked photo always stays, whatever its description says.
          const isFocus = focusMediaId && String(slide.dataset.mediaId) === String(focusMediaId);
          const show = isFocus || !named.some(Boolean) || named[picked];
          slide.hidden = !show;
          if (thumbs[i]) thumbs[i].hidden = !show;
          if (show) visibleIds.add(slide.dataset.mediaId);
        });

        // Never leave the gallery empty.
        if (visibleIds.size === 0) {
          slides.forEach((slide, i) => {
            slide.hidden = false;
            if (thumbs[i]) thumbs[i].hidden = false;
            visibleIds.add(slide.dataset.mediaId);
          });
        }

        vSlides = slides.filter((slide) => !slide.hidden);
        vThumbs = thumbs.filter((thumb) => !thumb.hidden);
        vSlides.forEach((slide, i) => slide.setAttribute('aria-label', i + 1 + ' of ' + vSlides.length));
        vThumbs.forEach((thumb, i) => thumb.setAttribute('aria-label', 'Show image ' + (i + 1)));
        if (totalEl) totalEl.textContent = String(vSlides.length);
        // Open on the variant's own photo; the first photo of the set only if it has none.
        let target = focusMediaId ? vSlides.findIndex((slide) => String(slide.dataset.mediaId) === String(focusMediaId)) : -1;
        if (target < 0) target = 0;
        active = -1;
        // Jump straight there: no smooth scroll and no scroll-snap while the slides are rearranged,
        // otherwise a scroll already under way (from the last colour) can land on the wrong photo.
        const targetSlide = vSlides[target];
        track.style.scrollBehavior = 'auto';
        track.style.scrollSnapType = 'none';
        track.scrollLeft = targetSlide ? targetSlide.offsetLeft : 0;
        void track.offsetWidth;
        requestAnimationFrame(() => {
          track.scrollLeft = targetSlide ? targetSlide.offsetLeft : 0;
          track.style.scrollBehavior = '';
          track.style.scrollSnapType = '';
          setActive(target);
        });
        setActive(target);
        root.dispatchEvent(new CustomEvent('gx:gallery-filter', { detail: { ids: visibleIds } }));
      },
    };
  }

  function initLightbox(root) {
    const dialog = root.querySelector('[data-gx-lightbox]');
    if (!dialog) return;

    const track = dialog.querySelector('[data-gx-lb-track]');
    const slideEls = [...track.querySelectorAll('[data-gx-lb-slide]')];
    const dots = [...dialog.querySelectorAll('[data-gx-lb-dot]')];
    const counter = dialog.querySelector('[data-gx-lb-counter]');
    const zoomButton = dialog.querySelector('[data-gx-lb-zoom]');
    const totalEl = dialog.querySelector('[data-gx-lb-total]');
    let vs = slideEls;
    let count = vs.length;
    let active = 0;

    const zooms = slideEls.map((slide) => {
      const img = slide.querySelector('[data-gx-lb-img]');
      return img ? createZoom(img, { max: 4 }) : null;
    });

    const activeZoom = () => zooms[slideEls.indexOf(vs[active])];

    const syncZoomState = () => {
      const zoomed = (activeZoom()?.scale || 1) > 1;
      dialog.classList.toggle('is-zoomed', zoomed);
      zoomButton?.setAttribute('aria-pressed', String(zoomed));
      zoomButton?.setAttribute('aria-label', zoomed ? 'Zoom out' : 'Zoom in');
    };

    const resetZooms = (except = -1) => {
      const exceptSlide = vs[except];
      zooms.forEach((zoom, i) => {
        if (zoom && slideEls[i] !== exceptSlide && zoom.scale > 1) zoom.reset(false);
      });
      syncZoomState();
    };

    const setActive = (index) => {
      if (index !== active) resetZooms(index);
      active = index;
      dots.forEach((dot, i) => dot.setAttribute('aria-current', String(slideEls[i] === vs[index])));
      if (counter) counter.textContent = String(index + 1);
      syncZoomState();
    };

    const go = (index) => {
      resetZooms();
      scrollToIndex(track, (index + count) % count);
    };

    track.addEventListener(
      'scroll',
      () => requestAnimationFrame(() => setActive(Math.min(count - 1, Math.max(0, currentIndex(track))))),
      { passive: true }
    );

    root.querySelectorAll('[data-gx-open-lightbox]').forEach((button) => {
      button.addEventListener('click', () => {
        const index = Math.max(0, vs.indexOf(slideEls[Number(button.dataset.gxOpenLightbox)]));
        resetZooms();
        dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
        // Load every lightbox image once it opens.
        track.querySelectorAll('img[loading="lazy"]').forEach((img) => img.setAttribute('loading', 'eager'));
        requestAnimationFrame(() => {
          scrollToIndex(track, index, false);
          setActive(index);
          track.focus({ preventScroll: true });
        });
      });
    });

    dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      resetZooms();
    });

    dialog.querySelector('[data-gx-lb-close]')?.addEventListener('click', () => dialog.close());
    dialog.querySelector('[data-gx-lb-prev]')?.addEventListener('click', () => go(active - 1));
    dialog.querySelector('[data-gx-lb-next]')?.addEventListener('click', () => go(active + 1));
    dots.forEach((dot) =>
      dot.addEventListener('click', () => go(Math.max(0, vs.indexOf(slideEls[Number(dot.dataset.gxLbDot)]))))
    );

    // A colour was chosen: keep only that colour's photos (and shared ones) in the viewer too.
    root.addEventListener('gx:gallery-filter', (event) => {
      const ids = event.detail.ids;
      slideEls.forEach((slide, i) => {
        const show = ids.has(slide.dataset.mediaId);
        slide.hidden = !show;
        if (dots[i]) dots[i].hidden = !show;
      });
      vs = slideEls.filter((slide) => !slide.hidden);
      count = vs.length;
      if (totalEl) totalEl.textContent = String(count);
      active = -1;
      setActive(0);
    });

    // Zoom button (keyboard and screen reader friendly).
    zoomButton?.addEventListener('click', () => {
      const zoom = activeZoom();
      if (!zoom) return;
      if (zoom.scale > 1) zoom.reset(true);
      else zoom.zoomCenter(2.5, true);
      syncZoomState();
    });

    slideEls.forEach((slide, i) => {
      const zoom = zooms[i];
      const img = slide.querySelector('[data-gx-lb-img]');
      if (!zoom || !img) return;

      // Mouse: click to zoom in at that spot, move to look around, click again to zoom out.
      img.addEventListener('click', (event) => {
        if (!canHover.matches) return;
        if (zoom.scale > 1) zoom.reset(true);
        else zoom.zoomAt(event.clientX, event.clientY, 2.5, true);
        syncZoomState();
      });
      slide.addEventListener('mousemove', (event) => {
        if (zoom.scale > 1 && canHover.matches) zoom.follow(event.clientX, event.clientY);
      });

      // Touch: pinch to zoom, drag to pan while zoomed, double-tap to toggle.
      attachTouchZoom(slide, zoom, {
        panWhenZoomed: true,
        onStart: () => dialog.classList.add('is-zoomed'),
        onEnd: () => {
          if (zoom.scale < 1.05) zoom.reset(true);
          syncZoomState();
        },
        onDoubleTap: (cx, cy) => {
          if (zoom.scale > 1) zoom.reset(true);
          else zoom.zoomAt(cx, cy, 2.5, true);
          syncZoomState();
        },
      });
    });

    dialog.addEventListener('keydown', (event) => {
      const zoom = activeZoom();
      if (!zoom) return;
      const zoomed = zoom.scale > 1;
      if (event.key === '+' || event.key === '=') {
        zoom.zoomCenter(Math.min(4, zoom.scale * 1.5));
      } else if (event.key === '-') {
        if (zoom.scale / 1.5 <= 1.05) zoom.reset(true);
        else zoom.zoomCenter(zoom.scale / 1.5);
      } else if (event.key === '0') {
        zoom.reset(true);
      } else if (zoomed && event.key.startsWith('Arrow')) {
        // While zoomed, arrow keys look around instead of changing image.
        event.preventDefault();
        const step = 80;
        const dx = event.key === 'ArrowLeft' ? step : event.key === 'ArrowRight' ? -step : 0;
        const dy = event.key === 'ArrowUp' ? step : event.key === 'ArrowDown' ? -step : 0;
        zoom.panBy(dx, dy);
      } else if (event.key === 'ArrowRight') {
        go(active + 1);
      } else if (event.key === 'ArrowLeft') {
        go(active - 1);
      }
      syncZoomState();
    });

    // Tapping the empty area around an image closes the viewer (unless zoomed).
    track.addEventListener('click', (event) => {
      if (event.target.matches('[data-gx-lb-slide]') && !dialog.classList.contains('is-zoomed')) dialog.close();
    });
  }

  /*
    3D models: the poster loads Shopify's 3D viewer on first tap. The "View in
    your space" button uses Shopify XR and only shows on devices that support AR.
  */
  const waitFor = (check, timeout = 4000) =>
    new Promise((resolve) => {
      const started = Date.now();
      const tick = () => {
        if (check()) resolve(true);
        else if (Date.now() - started > timeout) resolve(false);
        else setTimeout(tick, 100);
      };
      tick();
    });

  function initModels(root) {
    const shopify = window.Shopify;

    root.querySelectorAll('[data-gx-model]').forEach((wrap) => {
      const button = wrap.querySelector('[data-gx-model-load]');
      const template = wrap.querySelector('[data-gx-model-template]');
      if (!button || !template) return;

      button.addEventListener('click', () => {
        if (wrap.classList.contains('is-loaded')) return;
        wrap.appendChild(template.content.cloneNode(true));
        wrap.classList.add('is-loaded');
        const viewer = wrap.querySelector('model-viewer');
        shopify?.loadFeatures?.([
          {
            name: 'model-viewer-ui',
            version: '1.0',
            onLoad: async (errors) => {
              if (errors || !viewer) return;
              if (!(await waitFor(() => window.Shopify?.ModelViewerUI))) return;
              wrap.gxModelUI = new window.Shopify.ModelViewerUI(viewer);
              wrap.gxModelUI.play?.();
              viewer.focus?.();
            },
          },
        ]);
      });
    });

    const json = root.querySelector('[data-gx-model-json]');
    if (!json || !shopify?.loadFeatures) return;

    const setupXR = (errors) => {
      if (errors) return;
      if (!window.ShopifyXR) {
        document.addEventListener('shopify_xr_initialized', () => setupXR(), { once: true });
        return;
      }
      window.ShopifyXR.addModels(JSON.parse(json.textContent));
      window.ShopifyXR.setupXRElements();
    };
    shopify.loadFeatures([{ name: 'shopify-xr', version: '1.0', onLoad: setupXR }]);
  }

  function initVariants(root, gallery) {
    const picker = root.querySelector('[data-gx-variant-picker]');
    const dataEl = root.querySelector('[data-gx-variants]');
    if (!picker || !dataEl) return;

    const variants = JSON.parse(dataEl.textContent);
    const idInput = root.querySelector('[data-gx-variant-input]');
    const atc = root.querySelector('[data-gx-atc]');
    const atcLabel = root.querySelector('[data-gx-atc-label]');
    const atcText = (atc && atc.dataset.label) || (atcLabel ? atcLabel.textContent.trim() : 'Add to cart');
    const preorderEl = root.querySelector('[data-gx-preorder-note]');
    const preorderText = preorderEl ? preorderEl.dataset.preorderLabel || 'Pre-order' : '';
    const priceEl = root.querySelector('[data-gx-price]');
    const stickyPrice = root.querySelector('[data-gx-sticky-price]');
    const stickyAtc = root.querySelector('[data-gx-sticky-atc]');
    const stockEl = root.querySelector('[data-gx-stock]');
    const bundleEl = root.querySelector('[data-gx-bundle]');
    const colourIndex = root.dataset.gxColourIndex !== undefined ? Number(root.dataset.gxColourIndex) : -1;
    let colourNames = [];
    try {
      colourNames = JSON.parse(root.dataset.gxColours || '[]');
    } catch (e) {
      colourNames = [];
    }

    // Bundles: the crossed-out total, bundle price and "Save" line follow the selected colour.
    const updateBundleSaving = (variant) => {
      if (!variant.bundle_html) return;
      const target = bundleEl && bundleEl.querySelector('[data-gx-bundle-price]');
      if (target) target.innerHTML = variant.bundle_html;
    };

    // Low stock / made to order note for the selected colour (from Shopify inventory).
    const updateStock = (variant) => {
      if (!stockEl) return;
      let kind = '';
      let text = '';
      if (variant && variant.tracked) {
        const threshold = Number.parseInt(stockEl.dataset.threshold, 10) || 3;
        if (variant.qty > 0 && variant.qty <= threshold) {
          kind = 'low';
          text = (stockEl.dataset.lowText || '').replace('[count]', variant.qty);
        } else if (variant.qty <= 0 && variant.policy === 'continue') {
          kind = 'made';
          text = stockEl.dataset.madeText || '';
        }
      }
      stockEl.classList.toggle('gx-stock--low', kind === 'low');
      stockEl.classList.toggle('gx-stock--made', kind === 'made');
      stockEl.textContent = text;
      stockEl.hidden = !text;
    };

    // Chosen value per Shopify option, indexed by option position (the fieldsets may be shown in another order).
    const readSelected = () => {
      const chosen = [];
      picker.querySelectorAll('input:checked').forEach((input) => {
        chosen[Number(input.dataset.gxOption)] = input.value;
      });
      return chosen;
    };

    // Narrow the gallery to the chosen colour's photos plus the shared ones and show the variant's own photo.
    const syncGallery = (variant, selected, initial) => {
      let filtered = false;
      if (gallery && colourIndex > -1 && colourNames.length && selected[colourIndex]) {
        // On page load the gallery opens on photo 1; only a swatch click moves to the colour's own photo.
        gallery.filterColour(selected[colourIndex], colourNames, initial ? gallery.firstMediaId() : variant.media);
        filtered = true;
      }
      // Without colour filtering, just show the variant's own photo.
      if (!filtered && !initial && variant.media && gallery) gallery.showMedia(variant.media);
    };

    picker.addEventListener('change', () => {
      const selected = readSelected();
      const variant = variants.find((v) => v.options.every((option, i) => option === selected[i]));

      selected.forEach((value, i) => {
        const label = picker.querySelector(`[data-gx-option-label="${i}"]`);
        if (label) label.textContent = value;
      });

      if (!variant) {
        if (atc) atc.disabled = true;
        if (atcLabel) atcLabel.textContent = 'Unavailable';
        return;
      }

      if (idInput) idInput.value = variant.id;
      updateStock(variant);
      updateBundleSaving(variant);
      // Pre-order: no stock left but still sold (Continue selling when out of stock).
      const preorder = !!preorderEl && variant.available && variant.tracked && variant.qty <= 0 && variant.policy === 'continue';
      if (preorderEl) preorderEl.hidden = !preorder;
      if (atc) atc.disabled = !variant.available;
      if (atcLabel) atcLabel.textContent = preorder ? preorderText : variant.available ? atcText : 'Sold out';
      if (stickyAtc) {
        stickyAtc.disabled = !variant.available;
        stickyAtc.textContent = preorder ? preorderText : variant.available ? 'Add to cart' : 'Sold out';
      }

      if (priceEl && variant.bundle_html) {
        priceEl.innerHTML = variant.bundle_html;
      } else if (priceEl) {
        priceEl.innerHTML =
          `<span class="gx-price__current">${variant.price}</span>` +
          (variant.compare ? `<s class="gx-price__compare">${variant.compare}</s><span class="gx-price__badge">Sale</span>` : '');
      }
      if (stickyPrice) stickyPrice.textContent = variant.price;

      syncGallery(variant, selected);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    });

    // On page load the selected colour's photos are still the ones listed. The gallery opens on photo 1,
    // unless the link names a colour (?variant=, e.g. from an ad), then it opens on that colour's photo.
    const linkHasVariant = new URLSearchParams(window.location.search).has('variant');
    const initialSelected = readSelected();
    const initialVariant = variants.find((v) => v.options.every((option, i) => option === initialSelected[i]));
    if (initialVariant) syncGallery(initialVariant, initialSelected, !linkHasVariant);
  }

  /*
    Quantity steppers. The stepper inside the form owns the value (it is the
    field that gets submitted); the sticky bar's stepper mirrors it.
  */
  function initQuantity(root) {
    const steppers = [...root.querySelectorAll('[data-gx-qty]')];
    const main = steppers.find((el) => !el.hasAttribute('data-gx-qty-mirror'));
    if (!main) return;
    const inputs = steppers.map((el) => el.querySelector('[data-gx-qty-input]')).filter(Boolean);

    const clamp = (value) => Math.min(99, Math.max(1, Number.parseInt(value, 10) || 1));
    const setAll = (value) => {
      const qty = clamp(value);
      inputs.forEach((input) => {
        input.value = String(qty);
      });
      steppers.forEach((el) => {
        const minus = el.querySelector('[data-gx-qty-minus]');
        const plus = el.querySelector('[data-gx-qty-plus]');
        if (minus) minus.disabled = qty <= 1;
        if (plus) plus.disabled = qty >= 99;
      });
    };

    steppers.forEach((el) => {
      const input = el.querySelector('[data-gx-qty-input]');
      el.querySelector('[data-gx-qty-minus]')?.addEventListener('click', () => setAll(clamp(input.value) - 1));
      el.querySelector('[data-gx-qty-plus]')?.addEventListener('click', () => setAll(clamp(input.value) + 1));
      input?.addEventListener('change', () => setAll(input.value));
    });

    setAll(1);
  }

  function initSticky(root) {
    const bar = root.querySelector('[data-gx-sticky]');
    const atc = root.querySelector('[data-gx-atc]');
    if (!bar || !atc) return;

    const button = bar.querySelector('[data-gx-sticky-atc]');
    button?.addEventListener('click', () => {
      const form = atc.closest('form');
      if (form && !atc.disabled) form.requestSubmit(atc);
    });

    const focusables = [...bar.querySelectorAll('button, input')];
    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    let pastButton = false;
    let atFooter = false;
    const update = () => {
      const show = pastButton && !atFooter;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      document.documentElement.classList.toggle('gx-sticky-on', show);
      focusables.forEach((el) => {
        el.tabIndex = show ? 0 : -1;
      });
    };

    new IntersectionObserver(([entry]) => {
      pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      update();
    }).observe(atc);

    if (footer) {
      new IntersectionObserver(([entry]) => {
        atFooter = entry.isIntersecting;
        update();
      }).observe(footer);
    }
  }

  /*
    Feature hotspots: tap or click a marker to open its card (tap again, tap
    elsewhere or press Escape to close). With a mouse, hovering previews the card.
    Hovering a feature in the list highlights its marker.
  */
  function initHotspots(section) {
    const figure = section.querySelector('[data-gx-hotspots-figure]');
    if (!figure) return;

    const markers = [...figure.querySelectorAll('[data-gx-hotspot]')];
    const items = [...section.querySelectorAll('[data-gx-hotspot-item]')];
    let pinned = null;

    const card = (marker) => figure.querySelector(`[data-gx-hotspot-card="${marker.dataset.gxHotspot}"]`);

    const place = (marker, el) => {
      const fig = figure.getBoundingClientRect();
      const m = marker.getBoundingClientRect();
      const cx = m.left + m.width / 2 - fig.left;
      const cy = m.top + m.height / 2 - fig.top;
      const width = el.offsetWidth;
      const height = el.offsetHeight;
      const left = Math.min(Math.max(cx - width / 2, 8), fig.width - width - 8);
      // Open below the marker in the top half of the photo, above it in the bottom half.
      const below = cy < fig.height * 0.5;
      const top = below ? cy + 20 : cy - height - 20;
      el.style.left = `${Math.max(8, left)}px`;
      el.style.top = `${Math.max(-height, top)}px`;
    };

    const highlight = (n) => {
      items.forEach((item) => item.classList.toggle('is-active', item.dataset.gxHotspotItem === n));
      markers.forEach((marker) => marker.classList.toggle('is-active', marker.dataset.gxHotspot === n));
    };

    const close = (marker) => {
      if (!marker) return;
      marker.setAttribute('aria-expanded', 'false');
      const el = card(marker);
      if (el) el.hidden = true;
      if (pinned === marker) pinned = null;
      highlight(null);
    };

    const open = (marker) => {
      markers.forEach((other) => {
        if (other !== marker) close(other);
      });
      const el = card(marker);
      if (!el) return;
      el.hidden = false;
      place(marker, el);
      marker.setAttribute('aria-expanded', 'true');
      highlight(marker.dataset.gxHotspot);
    };

    markers.forEach((marker) => {
      marker.addEventListener('click', (event) => {
        event.stopPropagation();
        if (pinned === marker) {
          close(marker);
        } else {
          open(marker);
          pinned = marker;
        }
      });
      marker.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse' && !pinned) open(marker);
      });
      marker.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse' && pinned !== marker) close(marker);
      });
    });

    items.forEach((item) => {
      item.addEventListener('pointerenter', () => highlight(item.dataset.gxHotspotItem));
      item.addEventListener('pointerleave', () => {
        if (!pinned) highlight(null);
      });
    });

    document.addEventListener('click', (event) => {
      if (pinned && !figure.contains(event.target)) close(pinned);
    });
    section.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && pinned) {
        const marker = pinned;
        close(marker);
        marker.focus();
      }
    });
    window.addEventListener('resize', () => {
      if (pinned) place(pinned, card(pinned));
    });
  }

  /*
    Story videos: silent loops that play while on screen. The file is not downloaded until the
    video is within about 600px of the screen; the poster shows until then.
  */
  function initStoryVideos(scope) {
    scope.querySelectorAll('[data-gx-autoplay]').forEach((video) => {
      if (reduceMotion) return;
      const load = () => {
        if (!video.getAttribute('src') && video.dataset.src) video.src = video.dataset.src;
      };
      if (!('IntersectionObserver' in window)) {
        load();
        video.play().catch(() => {});
        return;
      }
      new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) load();
        },
        { rootMargin: '600px 0px' }
      ).observe(video);
      new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            load();
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        },
        { threshold: 0.25 }
      ).observe(video);
    });
  }

  /*
    "See It Packed" reel: it only starts loading when it scrolls near the screen, plays
    (silently, inline) while it is visible and pauses when it isn't. The round button
    turns the sound on and off.
  */
  function initPackedVideos(scope) {
    scope.querySelectorAll('[data-gx-packed]').forEach((section) => {
      if (section.dataset.gxReady) return;
      section.dataset.gxReady = 'true';
      const video = section.querySelector('[data-gx-packed-video]');
      const sound = section.querySelector('[data-gx-sound]');
      if (!video) return;

      if (sound) {
        const off = sound.querySelector('.gx-packed__sound-off');
        const on = sound.querySelector('.gx-packed__sound-on');
        sound.addEventListener('click', () => {
          video.muted = !video.muted;
          const soundOn = !video.muted;
          sound.setAttribute('aria-pressed', String(soundOn));
          sound.setAttribute('aria-label', soundOn ? 'Turn sound off' : 'Turn sound on');
          if (off) off.hidden = soundOn;
          if (on) on.hidden = !soundOn;
          if (soundOn && video.paused) video.play().catch(() => {});
        });
      }

      if (reduceMotion) {
        video.controls = true;
        if (sound) sound.hidden = true;
        return;
      }

      // Start fetching a little before it is on screen, play once it is.
      new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) video.preload = 'auto';
        },
        { rootMargin: '300px 0px' }
      ).observe(video);

      new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) video.play().catch(() => {});
          else video.pause();
        },
        { threshold: 0.35 }
      ).observe(video);
    });
  }

  /*
    Shopify Inbox chat. On phones its floating launcher would sit over the
    page content, so it is hidden there and the chat opens from our own
    buttons instead ("Questions? Chat with us" and the sticky bar icon).
    If Inbox isn't available the buttons stay hidden. Desktop is unchanged.
  */
  function initChat(root) {
    const triggers = [...root.querySelectorAll('[data-gx-chat]')];
    if (!triggers.length) return;

    // Inbox nests its launcher inside one or more shadow roots depending on version.
    const findLauncher = (root, depth = 0) => {
      const found = root.querySelector('button.activator');
      if (found) return { launcher: found, root };
      if (depth > 3) return null;
      for (const el of root.querySelectorAll('*')) {
        if (el.shadowRoot) {
          const hit = findLauncher(el.shadowRoot, depth + 1);
          if (hit) return hit;
        }
      }
      return null;
    };

    const locate = () => {
      const host = document.querySelector('shopify-chat');
      return host?.shadowRoot ? findLauncher(host.shadowRoot) : null;
    };

    // Inbox redraws its launcher (e.g. after the chat is closed), so the hiding
    // rule is re-applied whenever it's missing from the launcher's current root.
    const hideLauncher = () => {
      const hit = locate();
      if (hit && !hit.root.querySelector('#gx-chat-launcher')) {
        const style = document.createElement('style');
        style.id = 'gx-chat-launcher';
        style.textContent = '@media (max-width: 989px) { .activator-nudge, .activator { display: none !important; } }';
        hit.root.appendChild(style);
      }
      return hit;
    };

    let attempts = 0;
    const connect = () => {
      if (!hideLauncher()) {
        if (attempts++ < 80) setTimeout(connect, 250);
        return;
      }
      triggers.forEach((trigger) => {
        trigger.hidden = false;
        trigger.addEventListener('click', () => locate()?.launcher.click());
      });
      setInterval(hideLauncher, 500);
    };
    connect();
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-gx-product]').forEach((root) => {
      if (root.dataset.gxReady) return;
      root.dataset.gxReady = 'true';
      const gallery = initGallery(root);
      initLightbox(root);
      initModels(root);
      initVariants(root, gallery);
      initQuantity(root);
      initSticky(root);
      initChat(root);
    });

    scope.querySelectorAll('[data-gx-hotspots]').forEach((section) => {
      if (section.dataset.gxReady) return;
      section.dataset.gxReady = 'true';
      initHotspots(section);
    });

    initPackedVideos(scope);

    scope.querySelectorAll('[data-gx-story]').forEach((story) => {
      if (story.dataset.gxReady) return;
      story.dataset.gxReady = 'true';
      initStoryVideos(story);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme editor: re-run when a section is added or changed.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
